import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { X, Sparkles, Loader2, ArrowRight, AlertTriangle } from 'lucide-react';
import * as api from '../../services/api';
import HSCode from '../common/HSCode';
import MatchHighlighter from './MatchHighlighter';

function formatMoney(value) {
  if (value == null || value === '') return null;
  const num = parseFloat(value);
  if (Number.isNaN(num)) return null;
  return `$${num.toLocaleString('ru-RU', { maximumFractionDigits: 0 })}`;
}

function SimilarityBar({ score }) {
  if (score == null) return null;
  const percent = Math.min(Math.round(score * 100), 100);
  const tone =
    percent >= 95 ? 'text-violet-700 dark:text-violet-300' :
    percent >= 85 ? 'text-violet-600 dark:text-violet-400' :
    'text-violet-500 dark:text-violet-400';
  return (
    <div className="flex items-center gap-2 shrink-0">
      <span className={`text-[12px] font-bold tabular-nums ${tone}`}>{percent}%</span>
      <div className="w-12 h-1 bg-violet-100 dark:bg-violet-950/40 rounded-full overflow-hidden">
        <div
          className="h-full bg-violet-500 dark:bg-violet-400 rounded-full"
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}

function SimilarCard({ item, originalCode, searchQuery, onOpenAsMain }) {
  const codeDiffers = originalCode && item.product_code && item.product_code !== originalCode;
  const price = formatMoney(item.customs_value_usd);
  const date = item.declaration_date || item.declaration_year;

  return (
    <div
      className={`rounded-lg border p-3 transition-colors ${
        codeDiffers
          ? 'border-amber-200 bg-amber-50/40 dark:border-amber-900/50 dark:bg-amber-950/20'
          : 'border-border-default bg-elevated'
      }`}
    >
      {/* Top row: code + similarity + price */}
      <div className="flex items-center gap-2 flex-wrap">
        <HSCode code={item.product_code} size="md" commonPrefixLen={originalCode && item.product_code ? commonPrefixLength(originalCode, item.product_code) : 0} />
        {codeDiffers && (
          <span
            className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-amber-100 px-1.5 h-[18px] text-[10px] font-semibold text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-300"
            title="Класифіковано іншим кодом"
          >
            <AlertTriangle className="w-2.5 h-2.5" />
            ≠ код
          </span>
        )}
        <SimilarityBar score={item.relevance_score} />
        {price && (
          <span className="ml-auto text-[11px] font-mono tabular-nums text-text-secondary">
            {price}
          </span>
        )}
      </div>

      {/* Description — full, with highlighting */}
      {item.product_description_original && (
        <p className="mt-2 text-[12px] leading-relaxed text-text-primary">
          <MatchHighlighter
            text={item.product_description_original}
            query={searchQuery}
            searchMethod="similar"
            productCode={item.product_code}
          />
        </p>
      )}

      {/* Facts row */}
      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10.5px] text-text-muted">
        {item.country_of_origin && (
          <span>{item.country_of_origin}</span>
        )}
        {date && (
          <>
            {item.country_of_origin && <span className="text-text-disabled">·</span>}
            <span className="tabular-nums">{date}</span>
          </>
        )}
        {item.trademark && (
          <>
            <span className="text-text-disabled">·</span>
            <span className="truncate max-w-[140px]" title={item.trademark}>{item.trademark.trim()}</span>
          </>
        )}
        {item.importer_name && (
          <>
            <span className="text-text-disabled">·</span>
            <span className="truncate max-w-[180px]" title={item.importer_name}>{item.importer_name}</span>
          </>
        )}
      </div>

      {/* Open-as-main button */}
      <div className="mt-2 flex items-center justify-end">
        <button
          type="button"
          onClick={() => onOpenAsMain(item)}
          className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface px-2 h-6 text-[10.5px] font-medium text-text-secondary hover:bg-hover hover:text-text-primary transition-colors"
        >
          Відкрити як основний
          <ArrowRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

function commonPrefixLength(a, b) {
  if (!a || !b) return 0;
  let i = 0;
  while (i < a.length && i < b.length && a[i] === b[i]) i++;
  return i;
}

export default function SimilarRail({
  open,
  originalId,
  originalCode,
  searchQuery,
  onClose,
  onOpenAsMain,
  onCountChange,
}) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterCode, setFilterCode] = useState(null);

  useEffect(() => {
    if (!open || !originalId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setFilterCode(null);
    api.getSimilarProducts(originalId, 10)
      .then(data => {
        if (cancelled) return;
        const items = data.results || [];
        setResults(items);
        onCountChange?.(items.length);
      })
      .catch(err => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, originalId, onCountChange]);

  const codeStats = useMemo(() => {
    const counts = new Map();
    for (const item of results) {
      const code = item.product_code;
      if (!code) continue;
      counts.set(code, (counts.get(code) || 0) + 1);
    }
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  }, [results]);

  const filteredResults = useMemo(() => {
    if (!filterCode) return results;
    return results.filter(item => item.product_code === filterCode);
  }, [results, filterCode]);

  const handleOpenAsMain = useCallback((item) => {
    onOpenAsMain?.(item);
  }, [onOpenAsMain]);

  if (!open || !originalId) return null;

  return (
    <aside className="w-[440px] min-w-[280px] flex flex-col border-l border-border-default bg-surface animate-slide-in-right">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 border-b border-border-default px-4 py-2.5 flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="w-3.5 h-3.5 text-violet-600 dark:text-violet-400 shrink-0" />
          <span className="text-sm font-semibold text-text-primary">Схожі товари</span>
          {!loading && results.length > 0 && (
            <span className="text-[11px] text-text-muted tabular-nums">
              {results.length}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="grid h-7 w-7 place-items-center rounded-md text-text-muted hover:bg-hover hover:text-text-primary transition-colors flex-shrink-0"
          aria-label="Закрити схожі"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Code stats — only when 2+ different codes */}
      {!loading && codeStats.length >= 2 && (
        <div className="flex flex-wrap items-center gap-1.5 border-b border-border-default px-4 py-2 flex-shrink-0">
          <span className="text-[10px] uppercase tracking-wider text-text-muted">
            {codeStats.length} {codeStats.length === 2 ? 'коди' : 'кодів'}:
          </span>
          {codeStats.map(([code, count]) => {
            const isOriginal = code === originalCode;
            const active = filterCode === code;
            return (
              <button
                key={code}
                type="button"
                onClick={() => setFilterCode(active ? null : code)}
                className={`inline-flex items-center gap-1 rounded-md border px-1.5 h-[20px] text-[10.5px] font-mono transition-colors ${
                  active
                    ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-950/40 dark:text-blue-300'
                    : isOriginal
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-900/50 dark:bg-emerald-950/40 dark:text-emerald-300'
                      : 'border-border-default bg-elevated text-text-secondary hover:bg-hover'
                }`}
                title={isOriginal ? 'Той самий код, що і у вибраного товару' : `Код ${code}`}
              >
                {code}
                <span className="font-sans tabular-nums text-[10px] opacity-70">{count}</span>
              </button>
            );
          })}
          {filterCode && (
            <button
              type="button"
              onClick={() => setFilterCode(null)}
              className="ml-auto inline-flex items-center gap-1 text-[10.5px] text-text-muted hover:text-text-primary"
            >
              скинути
              <X className="w-2.5 h-2.5" />
            </button>
          )}
        </div>
      )}

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-y-auto thin-scroll px-4 py-3 space-y-2">
        {loading && (
          <div className="flex items-center justify-center gap-2 py-8 text-text-disabled text-xs">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Пошук схожих...
          </div>
        )}
        {error && (
          <p className="text-xs text-red-500 dark:text-red-400 py-2">{error}</p>
        )}
        {!loading && !error && results.length === 0 && (
          <p className="text-xs text-text-disabled text-center py-6">Схожих не знайдено</p>
        )}
        {!loading && filteredResults.map((item, i) => (
          <SimilarCard
            key={item.original_id || i}
            item={item}
            originalCode={originalCode}
            searchQuery={searchQuery}
            onOpenAsMain={handleOpenAsMain}
          />
        ))}
        {!loading && filterCode && filteredResults.length === 0 && (
          <p className="text-xs text-text-disabled text-center py-6">Немає товарів з цим кодом</p>
        )}
      </div>
    </aside>
  );
}
